// Protocolo do Hábito — "Como você está indo".
//
// O problema que este card resolve: "1 dia seguido · 2 dias lidos" não informa
// nada. A pessoa olha e não sabe se está bem, mal, nem o que acontece se
// continuar assim.
//
// Existem dois números publicados que respondem exatamente isso, e são eles a
// régua desta tela — nenhum inventado:
//
//   · 4 sessões por semana durante 6 semanas foi o mínimo para estabelecer
//     hábito (Kaushal & Rhodes 2015). A frequência importou; a duração não.
//   · Quem FORMOU o hábito executou em 88% dos dias; quem não formou ficou
//     entre 60% e 63% (Lally 2010).
//
// A régua é frequência, não volume, porque consistência é o preditor mais forte
// que existe (β = 0,21), acima de ambiente e de afeto.

import React, { useState } from 'react';
import { Icon } from '../icons';
import { EstadoHabito } from './api';

const serif = 'Instrument Serif, Georgia, serif';

const CHAVE_ABERTO = 'forjamente.habito.diagnostico.aberto';

const eyebrow: React.CSSProperties = {
  fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--ink-3)', fontWeight: 600,
};

/* ── A régua ──────────────────────────────────────────────────────────────
   Uma barra com DUAS marcas fixas: o limiar mínimo (4 de 7) e o ritmo de quem
   formou o hábito (88%). O ponto do usuário se posiciona entre elas. É isso que
   transforma "2 dias" em "2 dias, e o mínimo é 4". */
const Regua: React.FC<{ taxa: number; faixa: string }> = ({ taxa, faixa }) => {
  const pct = Math.max(0, Math.min(1, taxa));
  const limiar = 4 / 7;
  const alvo = 0.88;
  const cor = faixa === 'abaixo' ? '#E2574C' : faixa === 'no-limiar' ? 'var(--ember)' : '#7BAE7F';

  return (
    <div style={{ margin: '4px 0 14px' }}>
      <div style={{ position: 'relative', height: 10, borderRadius: 999, background: 'var(--paper-3)', overflow: 'visible' }}>
        <div style={{
          width: `${pct * 100}%`, height: '100%', borderRadius: 999,
          background: cor, transition: 'width .5s cubic-bezier(.2,.8,.2,1)',
        }} />
        {/* marca do limiar mínimo */}
        <div style={{ position: 'absolute', left: `${limiar * 100}%`, top: -4, bottom: -4, width: 2, background: 'var(--ink-3)', opacity: 0.6 }} />
        {/* marca do ritmo de quem formou */}
        <div style={{ position: 'absolute', left: `${alvo * 100}%`, top: -4, bottom: -4, width: 2, background: '#7BAE7F', opacity: 0.75 }} />
      </div>
      <div style={{ position: 'relative', height: 26, marginTop: 5 }}>
        <div style={{ position: 'absolute', left: `${limiar * 100}%`, transform: 'translateX(-50%)', textAlign: 'center', width: 74 }}>
          <div style={{ fontSize: 9.5, color: 'var(--ink-3)', lineHeight: 1.25 }}>mínimo<br />4 por semana</div>
        </div>
        <div style={{ position: 'absolute', left: `${alvo * 100}%`, transform: 'translateX(-50%)', textAlign: 'center', width: 78 }}>
          <div style={{ fontSize: 9.5, color: '#7BAE7F', lineHeight: 1.25 }}>quem formou<br />o hábito</div>
        </div>
      </div>
    </div>
  );
};

/* ── A primeira semana ────────────────────────────────────────────────────
   Antes de sete dias não existe leitura honesta: 1 de 4 dias vira "25%", e 25%
   parece um veredito quando é só ruído de amostra pequena. Então a régua não é
   desenhada ainda — no lugar dela, o quanto falta para ela ligar. A pessoa
   aprende que a régua existe sem ser julgada por ela. */
const Preludio: React.FC<{ dias: number }> = ({ dias }) => {
  const alvo = 7;
  const pct = Math.min(1, dias / alvo);
  return (
    <div style={{ margin: '4px 0 14px' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {Array.from({ length: alvo }, (_, i) => (
          <div key={i} style={{
            flex: 1, height: 10, borderRadius: 999,
            background: i < dias ? 'var(--ink-3)' : 'var(--paper-3)',
            border: i < dias ? 'none' : '1px dashed var(--line-strong)',
            opacity: i < dias ? 0.7 : 1,
          }} />
        ))}
      </div>
      <div style={{ fontSize: 9.5, color: 'var(--ink-4)', marginTop: 6 }}>
        {pct >= 1 ? 'a régua já pode ser lida' : `a régua liga no 7º dia — faltam ${alvo - dias}`}
      </div>
    </div>
  );
};

export const ComoVoceEstaIndo: React.FC<{
  estado: EstadoHabito;
  onVerPrograma?: () => void;
  onAbrirContrato?: () => void;
}> = ({ estado, onVerPrograma, onAbrirContrato }) => {
  // ── O CARD NASCE FECHADO ─────────────────────────────────────────────
  //
  // Ele responde bem a pergunta "como estou indo?", mas responde com seis
  // blocos de texto: veredito, parágrafo, ação, padrão de falta, perdão e
  // procedência dos números. Na Home, entre outros cards, isso é uma parede —
  // e uma parede de texto não se lê, se rola por cima.
  //
  // Fechado ele mostra o que se consulta todo dia: o número, a régua e a linha
  // do veredito. O resto é o porquê, e porquê se lê uma vez.
  //
  // A escolha FICA GUARDADA: quem abriu quer ver aberto amanhã também. Este
  // hook vem antes de qualquer retorno — abaixo há um `return null`, e um hook
  // depois dele só correria em parte das renderizações.
  const [aberto, setAberto] = useState(() => {
    try { return localStorage.getItem(CHAVE_ABERTO) === '1'; } catch { return false; }
  });
  const alternar = () => {
    const v = !aberto;
    setAberto(v);
    try { localStorage.setItem(CHAVE_ABERTO, v ? '1' : '0'); } catch {}
  };

  const d = estado.diagnostico;
  if (!d) return null;

  const pctTexto = `${Math.round(d.taxa * 100)}%`;
  const porSemana = String(d.porSemana).replace('.', ',');

  // Os dias da semana têm gêneros diferentes (o domingo, a segunda), então a
  // frase é montada SEM artigo — assim ela funciona com qualquer combinação.
  // Mais de três dias fracos não é padrão de dia nenhum: é a semana inteira, e
  // aí a informação útil é outra.
  const fracos = d.diasFracos.slice(0, 3);
  const listaFracos = fracos.length === 1
    ? fracos[0]
    : `${fracos.slice(0, -1).join(', ')} e ${fracos[fracos.length - 1]}`;
  const mostraFracos = d.diasFracos.length > 0 && d.diasFracos.length <= 3;

  // O veredito. Três coisas em ordem: onde você está, o que isso significa, e o
  // que acontece se continuar assim. Nada disso promete data.
  const veredito = d.cedoDemais
    ? {
        titulo: 'Ainda é cedo para um veredito',
        corpo: `Você leu em ${d.lidos} dos ${d.janela} primeiros ${d.janela === 1 ? 'dia' : 'dias'}. Com menos de uma semana qualquer porcentagem engana — 1 de 4 vira "25%" e parece diagnóstico quando é só amostra pequena. A partir do sétimo dia eu comparo seu ritmo com a régua e te digo o que ele significa. Por enquanto o que conta é aparecer.`,
        acao: null,
      }
    : d.faixa === 'alta'
      ? {
          titulo: 'Você está no ritmo de quem formou o hábito',
          corpo: `${d.lidos} de ${d.janela} dias — ${pctTexto}. No estudo original, quem terminou com o hábito formado executou em 88% dos dias. Você está nessa faixa. Se continuar assim, ler vai ficando automático — e a sensação de esforço cai sozinha.`,
          acao: null,
        }
      : d.faixa === 'no-limiar'
        ? {
            titulo: 'Você está acima do mínimo, abaixo do ideal',
            corpo: `${d.lidos} de ${d.janela} dias — ${pctTexto}, ou ${porSemana} por semana. Passou do mínimo de 4 por semana, que é onde o hábito começa a se firmar, mas ainda está longe dos 88% de quem terminou com ele formado. Continuar assim provavelmente funciona; só demora mais.`,
            acao: 'Um dia a mais por semana já te coloca na outra faixa.',
          }
        : {
            titulo: 'Nesse ritmo o hábito não se firma',
            corpo: `${d.lidos} de ${d.janela} dias — ${pctTexto}, ou ${porSemana} por semana. O mínimo medido para um hábito se estabelecer é 4 sessões por semana. Quem ficou em torno de 60% no estudo original terminou sem o hábito formado. Isso não é sentença: é o que acontece se nada mudar.`,
            acao: d.faltamPorSemana > 0
              ? `Faltam ${d.faltamPorSemana} ${d.faltamPorSemana === 1 ? 'dia' : 'dias'} por semana para você cruzar o mínimo.`
              : null,
          };

  const cor = d.cedoDemais ? 'var(--ink-3)'
    : d.faixa === 'abaixo' ? '#E2574C'
    : d.faixa === 'no-limiar' ? 'var(--ember)' : '#7BAE7F';

  return (
    <div style={{ padding: '20px 20px 0' }}>
      <div style={{
        background: 'var(--paper-2)', border: '1px solid var(--line)',
        borderRadius: 24, padding: 20,
      }}>
        {/* A FAIXA INTEIRA ABRE E FECHA, não um ícone de 12px no canto. O
            alvo de toque é a linha toda — num celular, mirar uma setinha é
            errar duas vezes antes de acertar. */}
        <button
          onClick={alternar}
          aria-expanded={aberto}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            width: '100%', marginBottom: 12, padding: 0, gap: 10,
            background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
          }}>
          <div className="mono" style={eyebrow}>Como você está indo</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
            <span style={{ fontFamily: serif, fontSize: 26, lineHeight: 1, color: cor }}>
              {d.cedoDemais ? `${d.lidos} de ${d.janela}` : pctTexto}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>dias</span>
            <span style={{
              display: 'inline-flex', marginLeft: 3,
              transform: aberto ? 'rotate(180deg)' : 'none', transition: 'transform .18s',
            }}>
              <Icon name="chevron-down" size={14} color="var(--ink-4)" />
            </span>
          </div>
        </button>

        {d.cedoDemais ? <Preludio dias={d.janela} /> : <Regua taxa={d.taxa} faixa={d.faixa} />}

        <div style={{ fontFamily: serif, fontSize: 21, lineHeight: 1.2, color: 'var(--ink)', marginBottom: 8 }}>
          {veredito.titulo}
        </div>
        {/* O TÍTULO DO VEREDITO FICA À MOSTRA, e isto não é meio-termo
            preguiçoso: sem ele o card fechado vira um número e uma barra, que
            é exatamente o "1 dia seguido · 2 dias lidos" que este arquivo
            existe para substituir. Interpretar é o trabalho do card; o resto
            é a explicação de como se chegou lá. */}
        {!aberto && (
          <button onClick={alternar} style={{
            display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: 'var(--ink-3)', fontSize: 12.5,
          }}>
            Ver o porquê
            <Icon name="chevron-down" size={12} color="var(--ink-4)" />
          </button>
        )}

        {aberto && (<>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0 }}>
          {veredito.corpo}
        </p>

        {veredito.acao && (
          <div style={{
            display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 14,
            padding: '12px 14px', borderRadius: 14, background: 'var(--paper)',
            border: '1px solid var(--line)',
          }}>
            <Icon name="target" size={15} color="var(--ember)" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink)' }}>{veredito.acao}</div>
          </div>
        )}

        {/* O padrão de falta. Saber QUAL dia some é acionável; saber que "faltou"
            não é. Só aparece com duas semanas cheias de dados — e só quando são
            poucos dias, porque cinco dias fracos não são padrão de dia nenhum,
            são a semana inteira, e aí a conversa é outra. */}
        {mostraFracos && (
          <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.55, color: 'var(--ink-2)' }}>
            Nas últimas duas semanas,{' '}
            <span style={{ color: 'var(--ember)' }}>{listaFracos}</span>
            {fracos.length === 1 ? ' passou' : ' passaram'} em branco todas as vezes.{' '}
            {onAbrirContrato ? (
              <button onClick={onAbrirContrato} style={{
                background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
                color: 'var(--ember)', textDecoration: 'underline', textUnderlineOffset: 3,
              }}>
                {fracos.length === 1 ? 'Seu gatilho acontece nesse dia?' : 'Seu gatilho acontece nesses dias?'}
              </button>
            ) : 'Vale checar se o seu gatilho acontece nesses dias.'}
          </div>
        )}

        {d.protegidos > 0 && (
          <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--ink-3)' }}>
            {d.protegidos === 1 ? 'Um dia' : `${d.protegidos} dias`} desta janela {d.protegidos === 1 ? 'foi coberto' : 'foram cobertos'} pelo perdão e não {d.protegidos === 1 ? 'conta' : 'contam'} contra você.
          </div>
        )}

        {onVerPrograma && (
          <button onClick={onVerPrograma} style={{
            display: 'flex', alignItems: 'center', gap: 6, marginTop: 14,
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            color: 'var(--ink-3)', fontSize: 12.5,
          }}>
            De onde vêm esses números
            <Icon name="chevron-right" size={12} color="var(--ink-4)" />
          </button>
        )}
        </>)}
      </div>
    </div>
  );
};

export default ComoVoceEstaIndo;
